import type { CustomElement } from "./utils.ts";

/**
 *
 */
export const extendElementWithActionAttributes = (Base: CustomElement): CustomElement =>
  class extends Base {
    #observer?: MutationObserver;

    connectedCallback(): void {
      super.connectedCallback?.();

      const update = (): void => {
        this.#updateHasActionChild();
        this.#updateHasActionChildChecked();
        this.#updateHasActionChildDisabled();
      };

      this.#observer = new MutationObserver(update);
      this.#observer.observe(this, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: [
          "tabindex",
          "disabled",
          "checked",
          "aria-current",
          "aria-pressed",
          "aria-checked",
        ],
      });

      update();

      const options = {
        passive: true,
      };
      this.addEventListener("pointerdown", this.#setHasActionChildActive, options);
      document.addEventListener("pointerup", this.#clearHasActionChildActive, options);
      document.addEventListener("pointercancel", this.#clearHasActionChildActive, options);

      /*                                                                       */
      /*                                                                        */
      /*                                                                         */
      /*                                              */
      document.addEventListener("change", this.#updateHasActionChildChecked, options);
    }

    disconnectedCallback(): void {
      super.disconnectedCallback?.();

      this.#observer?.disconnect();
      this.#observer = undefined;

      this.removeEventListener("pointerdown", this.#setHasActionChildActive);
      document.removeEventListener("pointerup", this.#clearHasActionChildActive);
      document.removeEventListener("pointercancel", this.#clearHasActionChildActive);

      document.removeEventListener("change", this.#updateHasActionChildChecked);
    }

    #updateHasActionChild = (): void => {
      this.toggleAttribute(
        "has-action-child",
        Array.from(this.children).some(
          (child) =>
            child.slot === "" &&
            child.matches('a[href], button, input, select, textarea, [tabindex="0"]'),
        ),
      );

      this.toggleAttribute(
        "has-action-child-enabled",
        Array.from(this.children).some(
          (child) =>
            child.slot === "" &&
            child.matches(
              'a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex="0"]:not(:disabled)',
            ),
        ),
      );
    };

    #setHasActionChildActive = (): void => {
      this.toggleAttribute("has-action-child-active", true);
    };

    #clearHasActionChildActive = (): void => {
      this.toggleAttribute("has-action-child-active", false);
    };

    #updateHasActionChildChecked = (): void => {
      this.toggleAttribute(
        "has-action-child-checked",
        Array.from(this.children).some(
          (child) =>
            child.slot === "" &&
            ((child as HTMLInputElement).checked ||
              (child.hasAttribute("aria-current") &&
                child.getAttribute("aria-current") !== "false") ||
              child.getAttribute("aria-pressed") === "true" ||
              child.getAttribute("aria-checked") === "true"),
        ),
      );
    };

    #updateHasActionChildDisabled = (): void => {
      this.toggleAttribute(
        "has-action-child-disabled",
        Array.from(this.children).some(
          (child) => child.slot === "" && (child as HTMLInputElement).disabled,
        ),
      );
    };
  };
